/**
 * PostHog React hooks for better integration with React components
 * These hooks provide a more React-idiomatic way to use PostHog
 */

import { usePostHog as usePostHogReact } from 'posthog-js/react';
import { useCallback } from 'react';
import { PostHogEventProperties } from '../services/postHogTypes';
import { getActiveTenantId, getActiveDomainId, getActiveSubjectId } from '../stores/contextStore';

/**
 * Enhanced PostHog hook with automatic context injection
 * Provides all PostHog methods with our custom context handling
 */
export const usePostHog = () => {
  const posthog = usePostHogReact();

  /**
   * Capture event with automatic context injection
   */
  const captureEvent = useCallback(
    (
      eventName: string,
      properties?: PostHogEventProperties,
      options?: { skipAutoContext?: boolean }
    ) => {
      if (!posthog) {
        return;
      }

      // Auto-inject context IDs if not explicitly disabled
      const finalProperties = { ...properties };

      if (!options?.skipAutoContext) {
        // Only add context IDs if they're not already provided in properties
        if (
          !finalProperties.tenant_id &&
          !finalProperties.domain_id &&
          !finalProperties.subject_id
        ) {
          const tenantId = getActiveTenantId();
          const domainId = getActiveDomainId();
          const subjectId = getActiveSubjectId();

          if (tenantId) finalProperties.tenant_id = tenantId;
          if (domainId) finalProperties.domain_id = domainId;
          if (subjectId) finalProperties.subject_id = subjectId;
        }
      }

      posthog.capture(eventName, finalProperties);
    },
    [posthog]
  );

  /**
   * Capture page view - for manual SPA page tracking
   */
  const capturePageView = useCallback(
    (pageName?: string, properties?: PostHogEventProperties) => {
      if (!posthog) return;

      posthog.capture('$pageview', {
        ...properties,
        $current_url: window.location.href,
        $pathname: window.location.pathname,
        page_name: pageName,
      });
    },
    [posthog]
  );

  /**
   * Identify user with properties
   */
  const identify = useCallback(
    (distinctId: string, properties?: Record<string, any>) => {
      if (!posthog) return;
      posthog.identify(distinctId, properties);
    },
    [posthog]
  );

  /**
   * Set super properties that persist across events
   */
  const setSuperProperties = useCallback(
    (properties: PostHogEventProperties) => {
      if (!posthog) return;
      posthog.register(properties);
    },
    [posthog]
  );

  /**
   * Reset user (useful for logout)
   */
  const reset = useCallback(() => {
    if (!posthog) return;

    // Track logout before resetting
    posthog.capture('user_logged_out');
    posthog.reset();
  }, [posthog]);

  /**
   * Set group for hierarchical analytics
   */
  const setGroup = useCallback(
    (groupType: string, groupId: string, properties?: Record<string, any>) => {
      if (!posthog) return;
      posthog.group(groupType, groupId, properties);
    },
    [posthog]
  );

  /**
   * Opt out of tracking
   */
  const optOut = useCallback(() => {
    if (!posthog) return;
    posthog.opt_out_capturing();
  }, [posthog]);

  /**
   * Opt in to tracking
   */
  const optIn = useCallback(() => {
    if (!posthog) return;
    posthog.opt_in_capturing();
  }, [posthog]);

  /**
   * Check if opted out
   */
  const hasOptedOut = useCallback(() => {
    if (!posthog) return false;
    return posthog.has_opted_out_capturing();
  }, [posthog]);

  /**
   * Capture error/exception with automatic context injection
   */
  const captureException = useCallback(
    (
      error: Error,
      properties?: PostHogEventProperties,
      options?: { skipAutoContext?: boolean }
    ) => {
      if (!posthog) {
        return;
      }

      // Auto-inject context IDs if not explicitly disabled
      const finalProperties = { ...properties };

      if (!options?.skipAutoContext) {
        // Only add context IDs if they're not already provided in properties
        if (
          !finalProperties.tenant_id &&
          !finalProperties.domain_id &&
          !finalProperties.subject_id
        ) {
          const tenantId = getActiveTenantId();
          const domainId = getActiveDomainId();
          const subjectId = getActiveSubjectId();

          if (tenantId) finalProperties.tenant_id = tenantId;
          if (domainId) finalProperties.domain_id = domainId;
          if (subjectId) finalProperties.subject_id = subjectId;
        }
      }

      // Basic error context
      finalProperties.error_name = error.name;
      finalProperties.error_message = error.message;
      finalProperties.error_stack = error.stack;
      finalProperties.url = window.location.href;
      finalProperties.user_agent = navigator.userAgent;
      finalProperties.timestamp = new Date().toISOString();
      finalProperties.development_mode = import.meta.env.DEV;

      posthog.captureException(error, finalProperties);
    },
    [posthog]
  );

  /**
   * Capture error with enhanced context (wrapper for captureException)
   */
  const captureError = useCallback(
    (error: Error, context?: Record<string, any>) => {
      const properties: PostHogEventProperties = {
        ...context,
        error_type: 'manual_capture',
      };

      captureException(error, properties);
    },
    [captureException]
  );

  /**
   * Add breadcrumb for user actions and debugging
   */
  const addBreadcrumb = useCallback(
    (message: string, category: string, data?: Record<string, any>) => {
      if (!posthog) return;

      // PostHog doesn't have traditional breadcrumbs like Sentry,
      // so we'll capture as events with breadcrumb prefix
      captureEvent('breadcrumb', {
        breadcrumb_message: message,
        breadcrumb_category: category,
        breadcrumb_data: JSON.stringify(data || {}),
        breadcrumb_timestamp: new Date().toISOString(),
      });
    },
    [captureEvent, posthog]
  );

  return {
    // Core PostHog instance for advanced usage
    posthog,

    // Enhanced methods with context injection
    captureEvent,
    capturePageView,
    identify,
    setSuperProperties,
    reset,
    setGroup,

    // Error tracking methods
    captureException,
    captureError,
    addBreadcrumb,

    // Privacy controls
    optOut,
    optIn,
    hasOptedOut,

    // Direct access to PostHog methods
    capture: posthog?.capture?.bind(posthog),
    alias: posthog?.alias?.bind(posthog),
    setPersonProperties: posthog?.setPersonProperties?.bind(posthog),
    startSessionRecording: posthog?.startSessionRecording?.bind(posthog),
    stopSessionRecording: posthog?.stopSessionRecording?.bind(posthog),
    sessionRecordingStarted: posthog?.sessionRecordingStarted?.bind(posthog),
  };
};
